import { useQuery } from '@tanstack/react-query';
import { McButton } from '@maersk-global/mds-react-wrapper';
import { demandsApi } from '../api/demandsApi';
import { formatCost } from '../utils/helpers/statusHelpers';
import type { ExecutorType } from '../types/engineering';

interface CostDetailModalProps {
  demandId: string;
  onClose: () => void;
}

const EXECUTOR_LABELS: Record<ExecutorType, string> = {
  ARCHITECT: 'Architect AI',
  PLANNER: 'Planner AI',
  IMPLEMENTER: 'Implementer AI',
  VULNERABILITY_SCAN: 'Vulnerability Scan',
  TESTER: 'Tester AI',
};

export const CostDetailModal = ({ demandId, onClose }: CostDetailModalProps) => {
  const { data: details = [], isLoading } = useQuery({
    queryKey: ['demand-cost-details', demandId],
    queryFn: () => demandsApi.getCostDetails(demandId),
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-panel"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '560px', width: '100%' }}
      >
        <div className="modal-panel__header">
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Cost Breakdown</h2>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-panel__body">
          {isLoading ? (
            <p style={{ fontSize: '0.875rem', color: 'var(--mds_brand_appearance_neutral_weak_text-color)' }}>
              Loading...
            </p>
          ) : details.length === 0 ? (
            <p style={{ fontSize: '0.875rem', color: 'var(--mds_brand_appearance_neutral_weak_text-color)' }}>
              No cost records found.
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--mds_brand_appearance_neutral_weak_border-color, #e0e0e0)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Type</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Cost</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Model</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {details.map((d) => (
                  <tr
                    key={d.id}
                    style={{ borderBottom: '1px solid var(--mds_brand_appearance_neutral_weak_border-color, #f0f0f0)' }}
                  >
                    <td style={{ padding: '6px 8px' }}>
                      {EXECUTOR_LABELS[d.executorType] ?? d.executorType}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {formatCost(d.cost)}
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--mds_brand_appearance_neutral_weak_text-color)' }}>
                      {d.llmModel ?? '—'}
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--mds_brand_appearance_neutral_weak_text-color)', whiteSpace: 'nowrap' }}>
                      {d.insertDate ? new Date(d.insertDate).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--mds_brand_appearance_neutral_weak_border-color, #e0e0e0)' }}>
                  <td style={{ padding: '6px 8px', fontWeight: 600 }}>Total</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {formatCost(details.reduce((sum, d) => sum + (d.cost ?? 0), 0))}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          )}
          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
            <McButton label="Close" appearance="neutral" variant="outlined" fit="small" click={onClose} />
          </div>
        </div>
      </div>
    </div>
  );
};
